import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  DashboardEnrollmentPresenter,
  EnrollmentPeriod,
  EnrollmentStage,
  EnrollmentType,
  ProductType,
  Proposal,
} from 'types/enrollmentService';

export function renewalProductLineName(sponsorId: string, productLineId: ProductLineId): string {
  return `${sponsorId}_RENEWAL_${productLineId}`;
}

/**
 * Product lines the group already has on a renewal/installed proposal.
 * Used so carriers that disallow change-plan cannot quote those lines again.
 * Sold periods are not included: carriers without renewal still need to quote those lines as a new sale.
 */
export function getCoveredProductLines(periods: EnrollmentPeriod[]): ProductLineId[] {
  return periods
    .filter((period) => !!period.installedProposal)
    .map((period) => period.installedProposal?.productLines.map((line) => line.id) || [])
    .flat();
}

/**
 * Returns enrollment periods that have the 'hasConfirmedProposal' property set to true.
 */
export function getActiveEPeriods(enrollmentPeriods: EnrollmentPeriod[]): EnrollmentPeriod[] {
  return enrollmentPeriods.filter((enrollmentPeriod) => enrollmentPeriod.hasConfirmedProposal);
}

/**
 * Returns enrollment periods with the types 'NEW_SALE' or 'RESUBMIT_NEW_SALE'.
 */
export function getNewSaleEPeriods(enrollmentPeriods: EnrollmentPeriod[]): EnrollmentPeriod[] {
  return enrollmentPeriods.filter(
    (enrollmentPeriod) =>
      enrollmentPeriod.type === EnrollmentType.NEW_SALE || enrollmentPeriod.type === EnrollmentType.RESUBMIT_NEW_SALE,
  );
}

/**
 * Returns enrollment periods that have the 'stage' property set to 'QUOTING'.
 */
export function getQuotingPeriods(enrollmentPeriods: EnrollmentPeriod[]): EnrollmentPeriod[] {
  return enrollmentPeriods.filter((enrollmentPeriod) => enrollmentPeriod.stage === EnrollmentStage.QUOTING);
}

export function mapEnrollmentsToGroups(
  enrollments: DashboardEnrollmentPresenter[],
): Record<string, DashboardEnrollmentPresenter[]> {
  return (
    enrollments.reduce((acc, current) => {
      const groupId = current.groupId;
      if (!acc[groupId]) {
        acc[groupId] = [];
      }
      acc[groupId].push(current);
      return acc;
    }, {} as Record<string, DashboardEnrollmentPresenter[]>) || {}
  );
}

export function isRenewOrCancelProductLine(productLineId: ProductLineId, confirmedProposal?: Proposal | null): boolean {
  if (confirmedProposal) {
    const productLine = confirmedProposal.productLines.find((line) => line.id === productLineId);
    return (
      !!productLine && (productLine.productType === ProductType.RENEW || productLine.productType === ProductType.CANCEL)
    );
  }
  return false;
}
